import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useUser } from "@clerk/clerk-react";
import { AppShell, AppSidebar } from "@/components/sidebar/AppSidebar";
import { useClubDetails, useClubGatherings } from "@/lib/clubs/gatherings";
import type { OrganizerClub } from "@/lib/clubs/queries";
import {
  getLastVisitedClubId,
  useLastVisitedSorted,
} from "@/lib/clubs/lastVisited";
import { relativeDay, shortTime } from "@/lib/format";
import styles from "./MultiClubHome.module.scss";

interface Props {
  clubs: OrganizerClub[];
}

export function MultiClubHome({ clubs }: Props) {
  const sortedClubs = useLastVisitedSorted(clubs);
  const [selectedId, setSelectedId] = useState<string>(
    () =>
      getLastVisitedClubId(clubs.map((c) => c.id)) ??
      sortedClubs[0]?.id ??
      clubs[0].id,
  );
  useEffect(() => {
    if (!clubs.some((c) => c.id === selectedId)) {
      setSelectedId(clubs[0].id);
    }
  }, [clubs, selectedId]);

  const selected =
    clubs.find((c) => c.id === selectedId) ?? sortedClubs[0] ?? clubs[0];

  return (
    <AppShell
      sidebar={
        <AppSidebar
          mode="preview"
          clubs={sortedClubs}
          selectedId={selected.id}
          onSelect={setSelectedId}
        />
      }
    >
      <div className={styles.canvas}>
        <Canvas key={selected.id} club={selected} />
      </div>
    </AppShell>
  );
}

interface CanvasProps {
  club: OrganizerClub;
}

function Canvas({ club }: CanvasProps) {
  const { user } = useUser();
  const detailsQuery = useClubDetails(club.id);
  const gatheringsQuery = useClubGatherings(club.id, { upcoming: true });

  const details = detailsQuery.data;
  const upcoming = (gatheringsQuery.data?.gatherings ?? [])
    .filter((g) => !g.cancelledAt)
    .sort((a, b) => a.date.localeCompare(b.date));
  const next = upcoming[0];
  const activeRoom = details?.readingRooms.find((r) => r.status === "ACTIVE");
  const firstName = user?.firstName ?? "there";

  return (
    <>
      <header className={styles.canvasHead}>
        <p className={styles.eyebrow}>Welcome back, {firstName}</p>
        <h1 className={styles.canvasTitle}>{club.name}</h1>
        {details?.description && (
          <p className={styles.canvasSub}>{details.description}</p>
        )}
      </header>

      <section className={styles.summary}>
        <div className={styles.summaryCard}>
          <span className={styles.cardLabel}>Currently reading</span>
          {activeRoom?.book ? (
            <>
              <h2 className={styles.cardValue}>{activeRoom.book.title}</h2>
              <p className={styles.cardMeta}>
                {activeRoom.book.author ?? "Unknown author"}
              </p>
            </>
          ) : (
            <p className={styles.cardEmpty}>
              {detailsQuery.isLoading ? "Loading…" : "No active room yet"}
            </p>
          )}
        </div>

        <div className={styles.summaryCard}>
          <span className={styles.cardLabel}>Next gathering</span>
          {next ? (
            <>
              <h2 className={styles.cardValue}>
                {relativeDay(next.date)} · {shortTime(next.date)}
              </h2>
              <p className={styles.cardMeta}>
                {[next.venueName, next.neighborhood]
                  .filter(Boolean)
                  .join(" · ") || "Venue TBD"}
              </p>
            </>
          ) : (
            <p className={styles.cardEmpty}>
              {gatheringsQuery.isLoading
                ? "Loading…"
                : "Nothing on the calendar."}
            </p>
          )}
        </div>

        <div className={styles.summaryCard}>
          <span className={styles.cardLabel}>Members</span>
          <h2 className={styles.cardValue}>{details?.memberCount ?? "—"}</h2>
          <p className={styles.cardMeta}>
            {details?.readingRooms.length ?? 0} reading room
            {details?.readingRooms.length === 1 ? "" : "s"}
          </p>
        </div>
      </section>

      <section className={styles.openBlock}>
        <h3 className={styles.blockHead}>Open {club.name}</h3>
        <div className={styles.linkGrid}>
          <Link
            to="/clubs/$id"
            params={{ id: club.id }}
            className={styles.linkCard}
          >
            <span className={styles.linkLabel}>Overview</span>
            <span className={styles.linkArrow}>→</span>
          </Link>
          <Link
            to="/clubs/$id/gatherings"
            params={{ id: club.id }}
            className={styles.linkCard}
          >
            <span className={styles.linkLabel}>
              Gatherings · {upcoming.length} upcoming
            </span>
            <span className={styles.linkArrow}>→</span>
          </Link>
          <Link
            to="/clubs/$id/voting"
            params={{ id: club.id }}
            className={styles.linkCard}
          >
            <span className={styles.linkLabel}>Voting</span>
            <span className={styles.linkArrow}>→</span>
          </Link>
          {activeRoom ? (
            <Link
              to="/clubs/$id/rooms/$roomId"
              params={{ id: club.id, roomId: activeRoom.id }}
              className={styles.linkCard}
            >
              <span className={styles.linkLabel}>Reading rooms · 1 active</span>
              <span className={styles.linkArrow}>→</span>
            </Link>
          ) : (
            <Link
              to="/clubs/$id/rooms/new"
              params={{ id: club.id }}
              className={styles.linkCard}
            >
              <span className={styles.linkLabel}>New reading room</span>
              <span className={styles.linkArrow}>→</span>
            </Link>
          )}
        </div>
      </section>
    </>
  );
}
